import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, ArrowRight, ChevronLeft } from 'lucide-react';

const API_BASE      = process.env.REACT_APP_API_URL        || 'https://daytrader.thecreativecoefficient.com.au';
const GATEWAY_KEY   = process.env.REACT_APP_API_KEY        || '';
const ANTHROPIC_KEY = process.env.REACT_APP_ANTHROPIC_KEY  || '';

const SYSTEM = `You are the lead desk reviewer for Trueline, a professional automated day trading system. You have analyzed a trading session in full — all ticker decisions, entry calls, reassessment decisions, P&L outcomes, and systematic patterns are in your context. Answer questions with specific reference to the session data: cite candle details, volume figures, R-multiples, and decision reasoning where relevant. Be direct, analytical, and concise. This is a professional tool — no disclaimers or hedging.`;

const SAMPLE = {
  session_date: '2026-06-15',
  anchor_time: '10:15',
  run_id: '20260623_1522',
  session_note: 'Short-biased session with mixed outcomes',
  session_summary: {
    overall_session_quality: 'MIXED',
    session_character: 'Strong filtering with a critical reassessment gap on IONR.',
    strongest_decision: 'VSME SKIP — correctly identified the 330K thesis-kill candle and abandoned the plan immediately.',
    weakest_decision: 'IONR — entered on a full-body bear candle, then HOLDed through explicit thesis acknowledgment for six consecutive reassessments.',
    systematic_patterns: [
      'Zone-abandon SKIP logic firing correctly across ITRG, JRSH, DXST, KXIN.',
      'T1-pending gate preventing premature tightens on ASST (12 deterministic HOLDs).',
      'Momentum freshness guidance producing correct NB SKIP citing primary candle age.',
    ],
    improvement_priorities: [
      'Structural invalidation EXIT rule — once thesis failure is acknowledged in reasoning, EXIT must follow.',
      'Candle body filter at LONG entry — entering on a full bear candle body is structurally unsound.',
      'Volume asymmetry discipline applied equally at entry as at SKIP decisions.',
    ],
  },
  ticker_reviews: [
    {
      ticker: 'ASST', overall_trade_quality: 'GOOD', final_pnl: 315.65,
      entry_decisions: [{ touch: 1, decision: 'ENTER', quality: 'GOOD', critique: 'Clean ORB_HIGH_TEST entry with bullish confirmation candle.' }],
      management_decisions: [{ decision: 'HOLD', quality: 'GOOD', critique: 'T1-pending gate correctly prevented 12 premature tightens. Trade ran to data ceiling.' }],
    },
    {
      ticker: 'IONR', overall_trade_quality: 'POOR', final_pnl: -509.22,
      entry_decisions: [{ touch: 1, decision: 'ENTER', quality: 'POOR', critique: 'Entry on a full-body bear candle — sellers were active at activation level at 10:16.' }],
      management_decisions: [{ decision: 'HOLD', quality: 'POOR', critique: 'Model acknowledged thesis invalidation but produced 6 consecutive HOLDs. Stop at $4.12 eventually hit.' }],
    },
    {
      ticker: 'VSME', overall_trade_quality: 'CORRECT_SKIP', final_pnl: null,
      entry_decisions: [{ touch: 1, decision: 'SKIP', quality: 'GOOD', critique: 'Zone-abandon gate fired — 2+ consecutive closes past entry zone.' }],
      management_decisions: [],
    },
    {
      ticker: 'TLRY', overall_trade_quality: 'ACCEPTABLE', final_pnl: -11.83,
      entry_decisions: [{ touch: 1, decision: 'ENTER', quality: 'ACCEPTABLE', critique: 'Entered on thin volume at ORB LOW test. Marginal structural confirmation.' }],
      management_decisions: [{ decision: 'EXIT', quality: 'GOOD', critique: 'Clean exit at $5.07 limiting loss to -$11.83 after 10 HOLDs.' }],
    },
  ],
};

const FEATURES = [
  {
    title: 'Decision-level forensics',
    body: 'Walk through any entry, hold, or exit and understand the exact reasoning behind it — including what the model saw, and what it missed.',
  },
  {
    title: 'Pattern recognition',
    body: 'Surface systematic strengths and gaps across the full session. Not individual trades — structural tendencies in how the model reads setups.',
  },
  {
    title: 'P&L attribution',
    body: 'Tie outcomes to decision quality. Understand whether a loss was a bad decision or a correct decision that went against you.',
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildContext(d) {
  const ss = d.session_summary || {};
  let c = `SESSION: ${d.session_date || ''} ${d.anchor_time || ''} (run ${d.run_id || ''})\n`;
  c += `Quality: ${ss.overall_session_quality || ''}\nCharacter: ${ss.session_character || ''}\n\n`;
  c += 'TICKER OUTCOMES:\n';
  for (const t of d.ticker_reviews || []) {
    const pnl = t.final_pnl != null ? `P&L $${t.final_pnl.toFixed(2)}` : 'no trade';
    c += `${t.ticker} — ${t.overall_trade_quality} | ${pnl}\n`;
    for (const ed of t.entry_decisions || [])
      c += `  Touch#${ed.touch} ${ed.decision} [${ed.quality}]: ${ed.critique || ''}\n`;
    for (const md of t.management_decisions || [])
      c += `  Mgmt ${md.decision} [${md.quality}]: ${md.critique || ''}\n`;
  }
  c += `\nSTRONGEST: ${ss.strongest_decision || ''}\nWEAKEST: ${ss.weakest_decision || ''}\n`;
  c += `\nPATTERNS:\n${(ss.systematic_patterns || []).map(p => `- ${p}`).join('\n')}`;
  c += `\n\nPRIORITIES:\n${(ss.improvement_priorities || []).map(p => `- ${p}`).join('\n')}`;
  return c;
}

function qualityCls(q) {
  if (!q) return 'text-text-lo';
  switch (q.toUpperCase()) {
    case 'GOOD':
    case 'CORRECT_SKIP':
    case 'SOLID':      return 'text-signal';
    case 'POOR':       return 'text-warn';
    case 'MIXED':
    case 'ACCEPTABLE': return 'text-rule';
    default:           return 'text-text-lo';
  }
}

function formatFilename(f) {
  const m = f.match(/session_review_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})/);
  if (!m) return f.replace('.json', '');
  const [, yr, mo, dy, hr, mn] = m;
  const d = new Date(parseInt(yr), parseInt(mo) - 1, parseInt(dy));
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${dateStr} · ${hr}:${mn}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ReviewChat = () => {
  const [screen, setScreen]                   = useState('load');
  const [reviewFiles, setReviewFiles]         = useState([]);
  const [filesLoading, setFilesLoading]       = useState(true);
  const [filesError, setFilesError]           = useState('');
  const [reviewData, setReviewData]           = useState(null);
  const [messages, setMessages]               = useState([]);
  const [convHistory, setConvHistory]         = useState([]);
  const [inputText, setInputText]             = useState('');
  const [isSending, setIsSending]             = useState(false);
  const [currentFilename, setCurrentFilename] = useState(null);
  const [wasRestored, setWasRestored]         = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { fetchFiles(); }, []);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchFiles = async () => {
    setFilesLoading(true);
    setFilesError('');
    try {
      const r = await fetch(`${API_BASE}/api/v1/reviews`, {
        headers: { 'x-api-key': GATEWAY_KEY },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setReviewFiles(data.files || []);
    } catch (e) {
      setFilesError(`Could not load review list: ${e.message}`);
    } finally {
      setFilesLoading(false);
    }
  };

  const loadFile = async (filename) => {
    setFilesError('');
    try {
      const r = await fetch(`${API_BASE}/api/v1/reviews/${encodeURIComponent(filename)}`, {
        headers: { 'x-api-key': GATEWAY_KEY },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      startSession(data, filename);
    } catch (e) {
      setFilesError(`Could not load review: ${e.message}`);
    }
  };

  // ── Storage ───────────────────────────────────────────────────────────────

  const saveChat = (filename, msgs, hist) => {
    if (!filename) return;
    try {
      localStorage.setItem(`trueline_review_${filename}`, JSON.stringify({
        messages: msgs, convHistory: hist, savedAt: new Date().toISOString(),
      }));
    } catch (e) {}
  };

  const clearSavedChat = (filename) => {
    if (!filename) return;
    try { localStorage.removeItem(`trueline_review_${filename}`); } catch (e) {}
  };

  // ── AI ────────────────────────────────────────────────────────────────────

  const callAI = async (msgs) => {
    if (!ANTHROPIC_KEY) throw new Error('REACT_APP_ANTHROPIC_KEY is not set.');
    const r = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ANTHROPIC_KEY,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-6',
        max_tokens: 1000,
        system: SYSTEM,
        messages: msgs,
      }),
    });
    const d = await r.json();
    if (d.error) throw new Error(d.error.message);
    return d.content[0].text.trim();
  };

  const startSession = async (data, filename = null) => {
    const ctx = buildContext(data);
    setReviewData(data);
    setCurrentFilename(filename);
    setScreen('chat');

    if (filename) {
      try {
        const saved = localStorage.getItem(`trueline_review_${filename}`);
        if (saved) {
          const { messages: savedMsgs, convHistory: savedHist } = JSON.parse(saved);
          setMessages(savedMsgs);
          setConvHistory(savedHist);
          setWasRestored(true);
          return;
        }
      } catch (e) {}
    }

    setWasRestored(false);
    const initMsg = {
      role: 'user',
      content: `Here is the full session review data:\n\n${ctx}\n\nPlease give me a concise opening summary of this session — key outcomes, what went well, and the single most important thing to discuss.`,
    };
    const hist = [initMsg];
    setConvHistory(hist);
    setMessages([{ role: 'assistant', loading: true }]);
    try {
      const reply = await callAI(hist);
      const finalHist = [...hist, { role: 'assistant', content: reply }];
      const finalMsgs = [{ role: 'assistant', content: reply }];
      setConvHistory(finalHist);
      setMessages(finalMsgs);
      saveChat(filename, finalMsgs, finalHist);
    } catch (e) {
      setMessages([{ role: 'assistant', content: `Error: ${e.message}` }]);
    }
  };

  const send = async () => {
    const text = inputText.trim();
    if (!text || isSending) return;
    setInputText('');
    setIsSending(true);
    const newHist = [...convHistory, { role: 'user', content: text }];
    setConvHistory(newHist);
    setMessages(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', loading: true }]);
    try {
      const reply = await callAI(newHist);
      const updatedHist = [...newHist, { role: 'assistant', content: reply }];
      setConvHistory(updatedHist);
      setMessages(prev => {
        const next = [...prev.filter(m => !m.loading), { role: 'assistant', content: reply }];
        saveChat(currentFilename, next, updatedHist);
        return next;
      });
    } catch (e) {
      setMessages(prev => [
        ...prev.filter(m => !m.loading),
        { role: 'assistant', content: `Error: ${e.message}` },
      ]);
    }
    setIsSending(false);
  };

  const askAbout    = (ticker) => setInputText(`Walk me through the ${ticker} decisions — what happened and was it the right call?`);
  const handleKey   = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const reset = () => {
    setScreen('load'); setReviewData(null); setMessages([]); setConvHistory([]);
    setInputText(''); setCurrentFilename(null); setWasRestored(false);
  };

  const startFresh = () => {
    clearSavedChat(currentFilename);
    setWasRestored(false); setMessages([]); setConvHistory([]);
    startSession(reviewData, null);
  };

  // ── Load screen ───────────────────────────────────────────────────────────

  if (screen === 'load') {
    return (
      <div className="min-h-[calc(100vh-56px)] bg-ink crosshair-bg">
        <div className="mx-auto max-w-6xl px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_400px] lg:gap-16 items-start">

            {/* ── Hero copy ── */}
            <div>
              <span className="eyebrow block mb-5">AI Trader — Session Review</span>

              <h1 className="text-4xl font-bold leading-[1.1] text-text-hi mb-4 sm:text-5xl">
                Chat one-on-one<br />
                with your AI trader.
              </h1>

              {/* Brand rule */}
              <div className="rule-line w-10 mb-6" />

              <p className="text-text-lo text-base leading-relaxed max-w-lg mb-10">
                Every entry, hold, and exit decision from the session is loaded into context.
                Ask anything — the trader answers with specific reference to candle structure,
                volume data, and R-multiples. No summaries. No disclaimers. The actual reasoning.
              </p>

              {/* Feature items with gold rule accent */}
              <div className="space-y-7">
                {FEATURES.map(({ title, body }) => (
                  <div key={title} className="flex gap-4">
                    <div className="w-px flex-shrink-0 self-stretch bg-rule" />
                    <div>
                      <div className="text-sm font-semibold text-text-hi mb-1">{title}</div>
                      <div className="text-xs text-text-lo leading-relaxed">{body}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom attribution */}
              <div className="mt-12 flex items-center gap-3">
                <div className="h-px flex-1 bg-haze" />
                <span className="font-mono text-[10px] tracking-[0.2em] text-text-lo/50">
                  POWERED BY CLAUDE · SONNET
                </span>
                <div className="h-px flex-1 bg-haze" />
              </div>
            </div>

            {/* ── Session picker ── */}
            <div className="card-surface p-6">
              <span className="eyebrow block mb-5">Sessions</span>

              {filesLoading ? (
                <div className="flex items-center gap-2 text-sm text-text-lo py-4">
                  <RefreshCw className="w-4 h-4 animate-spin text-rule" />
                  Loading sessions…
                </div>
              ) : filesError ? (
                <div className="text-sm text-warn p-3 bg-warn/10 border border-warn/20 rounded mb-4">
                  {filesError}
                </div>
              ) : reviewFiles.length === 0 ? (
                <div className="text-sm text-text-lo p-3 border border-haze rounded mb-4">
                  No review files found in{' '}
                  <code className="font-mono text-xs text-rule">/root/stage4/reviews/</code>
                </div>
              ) : (
                <div className="space-y-2 mb-6">
                  {reviewFiles.map(f => (
                    <button
                      key={f}
                      onClick={() => loadFile(f)}
                      className="w-full flex items-center justify-between px-4 py-3.5 bg-ink border border-haze hover:border-rule rounded transition-colors text-left group"
                    >
                      <div>
                        <div className="text-sm font-medium text-text-hi group-hover:text-rule transition-colors">
                          {formatFilename(f)}
                        </div>
                        <div className="font-mono text-[11px] text-text-lo mt-1">{f}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-text-lo/30 group-hover:text-rule flex-shrink-0 transition-colors ml-4" />
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-4 pt-4 border-t border-haze">
                <button
                  onClick={fetchFiles}
                  className="flex items-center gap-1.5 font-mono text-[11px] tracking-widest text-text-lo hover:text-text-hi transition-colors"
                >
                  <RefreshCw className="w-3 h-3" /> REFRESH
                </button>
                <span className="text-haze select-none">|</span>
                <button
                  onClick={() => startSession(SAMPLE)}
                  className="font-mono text-[11px] tracking-widest text-rule hover:text-text-hi transition-colors"
                >
                  TRY SAMPLE
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ── Chat screen ───────────────────────────────────────────────────────────

  const ss      = reviewData?.session_summary || {};
  const tickers = reviewData?.ticker_reviews  || [];

  return (
    <div className="flex bg-ink" style={{ height: 'calc(100vh - 56px)' }}>

      {/* ── Sidebar ──────────────────────────────────────────────── */}
      <div className="w-56 flex-shrink-0 border-r border-haze bg-ink-soft flex flex-col overflow-hidden">

        <div className="px-4 py-4 border-b border-haze">
          <span className="eyebrow block mb-3">Session</span>
          <div className="font-mono text-xs text-text-hi">{reviewData?.session_date}</div>
          <div className="font-mono text-xs text-text-lo mt-0.5">{reviewData?.anchor_time}</div>
          <div className={`font-mono text-xs font-bold mt-2 ${qualityCls(ss.overall_session_quality)}`}>
            {ss.overall_session_quality}
          </div>
        </div>

        <div className="px-4 py-3 border-b border-haze">
          <span className="eyebrow block mb-2">Tickers</span>
          <div className="space-y-0.5">
            {tickers.map(t => {
              const pnl = t.final_pnl != null
                ? (t.final_pnl >= 0
                    ? `+$${Math.round(t.final_pnl)}`
                    : `-$${Math.round(Math.abs(t.final_pnl))}`)
                : '—';
              const pnlCls = t.final_pnl == null ? 'text-text-lo'
                : t.final_pnl >= 0 ? 'text-signal' : 'text-warn';
              return (
                <button
                  key={t.ticker}
                  onClick={() => askAbout(t.ticker)}
                  className="w-full flex items-center justify-between px-2 py-2 rounded hover:bg-haze transition-colors group"
                >
                  <span className="font-mono text-xs font-semibold text-text-hi group-hover:text-rule transition-colors">
                    {t.ticker}
                  </span>
                  <span className={`font-mono text-xs ${pnlCls}`}>{pnl}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-4 py-3 flex-1 overflow-y-auto">
          <span className="eyebrow block mb-3">Priorities</span>
          <div className="space-y-3">
            {(ss.improvement_priorities || []).map((p, i) => (
              <div key={i} className="flex gap-2">
                <span className="font-mono text-[10px] text-rule min-w-[14px] mt-0.5">{i + 1}.</span>
                <span className="text-xs text-text-lo leading-relaxed">
                  {p.length > 90 ? `${p.substring(0, 90)}…` : p}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="px-4 py-3 border-t border-haze">
          <button
            onClick={reset}
            className="flex items-center gap-1.5 font-mono text-[11px] tracking-widest text-text-lo hover:text-rule transition-colors"
          >
            <ChevronLeft className="w-3 h-3" /> SESSIONS
          </button>
        </div>
      </div>

      {/* ── Chat area ────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {wasRestored && (
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-2 bg-rule/10 border-b border-rule/30">
            <span className="font-mono text-[11px] tracking-widest text-rule">
              PREVIOUS CONVERSATION RESTORED
            </span>
            <button
              onClick={startFresh}
              className="font-mono text-[11px] tracking-widest text-text-lo hover:text-rule transition-colors"
            >
              START FRESH
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          {messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <span className="font-mono text-xs text-text-lo/30 tracking-widest">AWAITING SESSION DATA</span>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex items-start gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-0.5 self-stretch bg-rule rounded-full flex-shrink-0 min-h-8" />
              )}
              <div
                className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap break-words rounded ${
                  msg.role === 'user'
                    ? 'bg-haze text-text-hi'
                    : 'bg-ink-soft border border-haze text-text-hi'
                }`}
              >
                {msg.loading ? (
                  <span className="flex gap-1.5 items-center py-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-rule animate-pulse-soft" />
                    <span className="h-1.5 w-1.5 rounded-full bg-rule animate-pulse-soft" style={{ animationDelay: '0.3s' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-rule animate-pulse-soft" style={{ animationDelay: '0.6s' }} />
                  </span>
                ) : msg.content}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="flex-shrink-0 border-t border-haze px-4 py-3 flex gap-3 items-end bg-ink-soft">
          <textarea
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask about a ticker, decision, or pattern…"
            rows={2}
            disabled={isSending}
            className="flex-1 px-3 py-2.5 text-sm bg-ink border border-haze text-text-hi placeholder:text-text-lo/40 rounded resize-none focus:outline-none focus:border-rule transition-colors disabled:opacity-50"
          />
          <button
            onClick={send}
            disabled={isSending || !inputText.trim()}
            className={`px-5 py-2.5 rounded text-sm font-semibold flex-shrink-0 transition-colors ${
              isSending || !inputText.trim()
                ? 'bg-haze text-text-lo/50 cursor-not-allowed'
                : 'btn-amber'
            }`}
          >
            {isSending ? '···' : 'Send'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ReviewChat;
